import { Typography } from '@mui/material';
import {
  useGetAllPatientChallengesByID,
  useGetAllPatientExercisesByID,
  useGetPatientGoals,
} from '_api';
import {
  Challenge,
  CompletedItem,
  CompletedItemType,
  ContentSettingV2,
  Exercise,
} from '_api/types';
import { ExerciseRouteMap, ChallengeRouteMap, PatientRouteMap } from '_constants';
import { ActionsFlexBox } from 'components/molecules';
import dayjs from 'dayjs';
import { MRT_ColumnDef, MaterialReactTable } from 'material-react-table';
import React, { useMemo } from 'react';
import { useNavigateTo } from 'utils';

import { ProfileTableProps } from './types';

export const PatientCompletedTable = ({ patientId }: ProfileTableProps) => {
  const navigate = useNavigateTo();

  const { data: exercises, isLoading: isExerciseLoading } = useGetAllPatientExercisesByID(
    { patientId },
  );
  const { data: challenges, isLoading: isChallengeLoading } =
    useGetAllPatientChallengesByID(patientId);
  const { data: goals, isLoading: isGoalLoading } = useGetPatientGoals(patientId);

  const simplifySchedules = (
    item: ContentSettingV2<Exercise> | ContentSettingV2<Challenge>,
  ) => {
    item.assignedDate = item.schedules[0].startDate;
    item.completedDate = item.schedules[item.schedules.length - 1]!.endDate;
    return item;
  };

  const activePatientExercisesById = useMemo(() => {
    const exerciseItems = exercises ? exercises.filter((x) => x.status === 'DONE') : [];
    return exerciseItems.map((item) => ({
      data: simplifySchedules(item),
      type: 'EXERCISE',
    }));
  }, [exercises]);

  const activePatientChallengesById = useMemo(() => {
    const challengeItems = challenges
      ? challenges.filter((x) => x.status === 'DONE')
      : [];
    return challengeItems.map((item) => ({
      data: simplifySchedules(item),
      type: 'CHALLENGE',
    }));
  }, [challenges]);

  const activePatientGoalsById = useMemo(() => {
    const goalItems = goals ? goals.filter((x) => x.status.name === 'COMPLETED') : [];
    return goalItems.map((item) => ({
      data: item,
      type: 'GOAL',
    }));
  }, [goals]);

  const completedItems = [
    ...activePatientExercisesById,
    ...activePatientChallengesById,
    ...activePatientGoalsById,
  ];

  const handleReportOnClick = (id: number, type: string) => {
    switch (type) {
      case 'EXERCISE':
        navigate(ExerciseRouteMap.REPORT, id.toString());
        break;
      case 'CHALLENGE':
        navigate(ChallengeRouteMap.REPORT, id.toString());
        break;
      case 'GOAL':
        navigate(PatientRouteMap.REPORT, id.toString());
        break;
    }
  };

  const columns = useMemo<MRT_ColumnDef<CompletedItem<CompletedItemType>>[]>(
    () => [
      {
        header: 'Completed',
        accessorFn: (row) => {
          if (row.type === 'GOAL') {
            return row.data?.title;
          } else {
            return row.data?.content.title;
          }
        },
      },
      {
        header: 'Assigned Date',
        accessorFn: (row) => {
          if (row.type !== 'GOAL') {
            return dayjs(row.data?.assignedDate).format('MM/DD/YYYY');
          }
        },
        size: 10,
      },
      {
        header: 'Completed Date',
        accessorFn: (row) => {
          if (row.type !== 'GOAL') {
            return row.data?.completedDate
              ? dayjs(row.data?.completedDate).format('MM/DD/YYYY')
              : '';
          }
        },
        size: 10,
      },
      {
        header: 'Actions',
        accessorFn: (row) => (
          <ActionsFlexBox
            reportOnClick={() => handleReportOnClick(row.data.id, row.type)}
            actionButtons={{
              reportButton: true,
            }}
          />
        ),
        size: 10,
      },
    ],
    [completedItems],
  );

  return (
    <>
      <Typography variant="h4" sx={{ textAlign: 'left' }}>
        Completed
      </Typography>
      {completedItems && !isExerciseLoading && !isChallengeLoading && !isGoalLoading && (
        <MaterialReactTable
          // @ts-ignore
          data={completedItems}
          // @ts-ignore
          columns={columns}
          enableSorting={false}
          enableColumnActions={false}
          enableTopToolbar={false}
          enablePagination={true}
        />
      )}
    </>
  );
};
